// src/components/Store/StorePage.jsx
// Tienda estilo marketplace: buscador + departamentos, filtros laterales,
// cuadrícula de resultados y carrito deslizable. Se puede navegar sin cuenta;
// para pagar pide iniciar sesión (el carrito queda guardado).
// Los filtros viven en la URL (?q=&category=&price=&sort=) para poder
// compartir una búsqueda y volver desde la página de producto.
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import toast, { Toaster } from 'react-hot-toast';
import { ShoppingCartIcon } from '@heroicons/react/24/outline';
import { formatMXN, productName } from '../../utils/store';
import ProductFilter, { SORT_OPTIONS, PRICE_RANGES, inPriceRange } from './ProductFilter';
import ProductCard from './ProductCard';
import StoreHeader from './StoreHeader';
import CartDrawer from './CartDrawer';
import { useStoreProducts, useCart, maxQtyFor } from './useStore';

const BANNER_IMAGE =
  'https://res.cloudinary.com/dc6xthiol/image/upload/q_auto,f_auto,w_900/v1783546638/20251204_162826_008_doyi38.jpg';

const StorePage = () => {
  const { t, i18n } = useTranslation('common');
  const lang = i18n.language;
  const [params, setParams] = useSearchParams();
  const { products, loading, loadError } = useStoreProducts();
  const { cartItems, changeQty, removeFromCart, cartCount, cartTotal, qtyInCart } = useCart(products);

  const filters = {
    search: params.get('q') || '',
    category: params.get('category') || '',
    price: PRICE_RANGES[params.get('price')] ? params.get('price') : '',
    sort: SORT_OPTIONS.includes(params.get('sort')) ? params.get('sort') : 'featured',
  };
  const cartOpen = params.get('cart') === 'open';

  const updateParams = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)));
    setParams(next, { replace: true });
  };
  const setFilters = (f) =>
    updateParams({ q: f.search, category: f.category, price: f.price, sort: f.sort === 'featured' ? '' : f.sort });
  const setCartOpen = (open) => updateParams({ cart: open ? 'open' : '' });

  // Solo categorías que tienen productos, con su conteo
  const counts = useMemo(
    () => products.reduce((acc, p) => ({ ...acc, [p.category]: (acc[p.category] || 0) + 1 }), {}),
    [products]
  );
  const categories = Object.keys(counts);

  const visibleProducts = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    const list = products.filter(
      (p) =>
        (!filters.category || p.category === filters.category) &&
        inPriceRange(p.price, filters.price) &&
        (!search ||
          [p.name, p.description, p.nameEn, p.descriptionEn].some((text) => text?.toLowerCase().includes(search)))
    );
    const sorters = {
      priceAsc: (a, b) => a.price - b.price,
      priceDesc: (a, b) => b.price - a.price,
      name: (a, b) => productName(a, lang).localeCompare(productName(b, lang)),
    };
    return sorters[filters.sort] ? [...list].sort(sorters[filters.sort]) : list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, filters.search, filters.category, filters.price, filters.sort, lang]);

  // Al agregar por primera vez, confirmación con acceso directo al carrito
  const handleChangeQty = (productId, delta) => {
    const product = products.find((p) => p._id === productId);
    if (product && delta === 1 && qtyInCart(productId) === 0) {
      toast.success(
        (toastObj) => (
          <span className="flex items-center gap-3 text-sm">
            <span>
              <strong>{t('store.addedToCart')}</strong>
              <span className="block text-xs text-[#3A2A20]/60 line-clamp-1">{productName(product, lang)}</span>
            </span>
            <button
              type="button"
              onClick={() => {
                toast.dismiss(toastObj.id);
                setCartOpen(true);
              }}
              className="shrink-0 rounded-full border border-[#9A0002] px-3 py-1 text-xs font-semibold text-[#9A0002]"
            >
              {t('store.viewCart')}
            </button>
          </span>
        ),
        { id: 'added-to-cart', duration: 3000 }
      );
    }
    changeQty(productId, delta);
  };

  const selectClass =
    'rounded-lg border border-[#3A2A20]/20 bg-[#EFE6DD]/60 px-2.5 py-1.5 text-base sm:text-sm text-[#3A2A20] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#9A0002]/40';

  return (
    <div className="bg-[#EFE6DD] min-h-screen">
      <Helmet>
        <title>{t('store.metaTitle')}</title>
        <meta name="description" content={t('store.metaDescription')} />
        <link rel="canonical" href="https://www.hotelmacarios.com.mx/store" />
      </Helmet>

      <StoreHeader
        search={filters.search}
        category={filters.category}
        categories={categories}
        cartCount={cartCount}
        onOpenCart={() => setCartOpen(true)}
        onSearch={(q) => setFilters({ ...filters, search: q })}
        onCategory={(c) => setFilters({ ...filters, category: c })}
      />

      {/* Banner */}
      {!filters.search && !filters.category && (
        <div className="mx-auto max-w-7xl px-3 pt-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#9A0002] to-[#7A0001] text-[#EFE6DD] shadow-md">
            <img
              src={BANNER_IMAGE}
              alt=""
              className="absolute inset-y-0 right-0 h-full w-1/2 object-cover opacity-90 [mask-image:linear-gradient(to_right,transparent,black_45%)] sm:w-2/5"
            />
            <div className="relative max-w-[62%] p-5 sm:max-w-xl sm:p-8">
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#EFE6DD]/80">
                {t('store.eyebrow')}
              </span>
              <h1 className="mt-1 font-['Fraunces'] text-2xl font-semibold leading-tight sm:text-4xl">
                {t('store.title')}
              </h1>
              <p className="mt-2 hidden text-sm text-[#EFE6DD]/85 sm:block">{t('store.subtitle')}</p>
              <p className="mt-3 inline-flex rounded-full bg-[#EFE6DD] px-3 py-1 text-xs font-bold text-[#9A0002]">
                {t('store.freeDeliveryBadge')}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto flex max-w-7xl gap-6 px-3 py-4 sm:px-6 lg:px-8">
        {/* Filtros (desktop) */}
        <aside className="hidden w-52 shrink-0 lg:block">
          <ProductFilter filters={filters} onChange={setFilters} categories={categories} counts={counts} />
        </aside>

        <main className="min-w-0 flex-1">
          {/* Barra de resultados */}
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 shadow-sm">
            <p className="text-sm text-[#3A2A20]">
              {loading ? (
                t('common.loading')
              ) : (
                <>
                  {t('store.resultCount', { count: visibleProducts.length })}
                  {filters.search && (
                    <>
                      {' '}
                      {t('store.resultsFor')}{' '}
                      <span className="font-bold text-[#9A0002]">"{filters.search}"</span>
                    </>
                  )}
                </>
              )}
            </p>
            <div className="flex items-center gap-2">
              {/* Precio (móvil; en desktop está en la barra lateral) */}
              <select
                value={filters.price}
                onChange={(e) => setFilters({ ...filters, price: e.target.value })}
                aria-label={t('store.price')}
                className={`${selectClass} lg:hidden`}
              >
                <option value="">{t('store.price')}</option>
                {Object.keys(PRICE_RANGES).map((key) => (
                  <option key={key} value={key}>
                    {t(`store.priceRanges.${key}`)}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-1.5 text-sm text-[#3A2A20]/70">
                <span className="hidden sm:inline">{t('store.sortLabel')}:</span>
                <select
                  value={filters.sort}
                  onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
                  aria-label={t('store.sortLabel')}
                  className={selectClass}
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {t(`store.sort.${option}`)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="animate-pulse rounded-lg border border-[#3A2A20]/10 bg-white">
                  <div className="aspect-square bg-[#3A2A20]/5" />
                  <div className="space-y-2 p-4">
                    <div className="h-3 w-1/3 rounded bg-[#3A2A20]/10" />
                    <div className="h-4 w-full rounded bg-[#3A2A20]/10" />
                    <div className="h-6 w-1/2 rounded bg-[#3A2A20]/10" />
                  </div>
                </div>
              ))}
            </div>
          ) : loadError ? (
            <p className="rounded-lg bg-white py-16 text-center text-sm text-[#9A0002]">{t('store.loadError')}</p>
          ) : visibleProducts.length === 0 ? (
            <div className="rounded-lg bg-white py-16 text-center">
              <p className="text-sm text-[#3A2A20]/60">
                {products.length === 0 ? t('store.noProducts') : t('store.noMatches')}
              </p>
              {products.length > 0 && (
                <button
                  type="button"
                  onClick={() => setFilters({ ...filters, search: '', category: '', price: '' })}
                  className="mt-3 text-sm font-medium text-[#9A0002] hover:underline"
                >
                  {t('store.clearFilters')}
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 xl:grid-cols-4">
              {visibleProducts.map((product) => (
                <ProductCard
                  key={product._id}
                  product={product}
                  qty={qtyInCart(product._id)}
                  maxQty={maxQtyFor(product)}
                  onChangeQty={handleChangeQty}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Barra flotante del carrito (móvil) */}
      {cartCount > 0 && (
        <>
          <button
            type="button"
            onClick={() => setCartOpen(true)}
            className="fixed inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-20 flex items-center justify-between rounded-full bg-[#9A0002] px-5 py-3.5 text-[#EFE6DD] shadow-xl shadow-[#9A0002]/30 transition active:scale-[0.98] lg:hidden"
          >
            <span className="flex items-center gap-2 text-sm font-semibold">
              <ShoppingCartIcon className="h-5 w-5" aria-hidden="true" />
              {t('store.itemsInCart', { count: cartCount })}
            </span>
            <span className="text-sm font-semibold">{formatMXN(cartTotal)} →</span>
          </button>
          <div className="h-20 lg:hidden" aria-hidden="true" />
        </>
      )}

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cartItems={cartItems}
        onChangeQty={changeQty}
        onRemove={removeFromCart}
      />
      <Toaster position="top-center" />
    </div>
  );
};

export default StorePage;
